import { useState, useEffect } from 'react';
import { validateEmail, filterEmailInput } from '../utils/validation';
import styles from './ValidatedInput.module.css';

/**
 * Email input field with real-time validation and character filtering
 * - Enforces valid email characters only
 * - Prevents multiple @ symbols
 * - Shows temporary tooltip when invalid character is typed
 */
export function EmailInput({
    value,
    onChange,
    error,
    onValidationChange,
    placeholder = 'Email',
    disabled = false
}) {
    const [touched, setTouched] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [showInvalidCharTooltip, setShowInvalidCharTooltip] = useState(false);

    // Validate on change
    useEffect(() => {
        if (value) {
            const result = validateEmail(value);
            setValidationResult(result);
            if (onValidationChange) {
                onValidationChange(result.valid);
            }
        } else {
            setValidationResult(null);
            if (onValidationChange) {
                onValidationChange(false);
            }
        }
    }, [value, onValidationChange]);

    useEffect(() => {
        if (!showInvalidCharTooltip) return;

        const timer = setTimeout(() => {
            setShowInvalidCharTooltip(false);
        }, 1500);

        return () => clearTimeout(timer);
    }, [showInvalidCharTooltip]);

    const handleChange = (e) => {
        let newValue = e.target.value;
        const oldValue = value;

        // Check if the value changed after filtering
        const filtered = filterEmailInput(newValue, value);

        // If value was rejected (filtering removed characters), show tooltip
        if (filtered === oldValue && newValue !== oldValue) {
            setShowInvalidCharTooltip(true);
            return;
        }

        // Enforce max length
        newValue = filtered.slice(0, 255);

        onChange(newValue);
    };

    const handleBlur = () => {
        setTouched(true);
    };

    const showError = touched && validationResult && !validationResult.valid;
    const showSuccess = touched && validationResult && validationResult.valid;

    return (
        <div className={styles.fieldWrapper}>
            {showInvalidCharTooltip && (
                <div className={styles.tooltip}>
                    Tilladt: bogstaver, tal, . _ + -
                </div>
            )}
            <input
                type="email"
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
                className={`${styles.input} ${showError ? styles.inputError : ''} ${showSuccess ? styles.inputSuccess : ''}`}
                maxLength={255}
                autoComplete="email"
            />
            {value && (
                <div className={styles.charCount}>
                    {value.length}/255
                </div>
            )}
            {showError && (
                <div className={styles.errorMessage}>
                    ⚠️ {validationResult.error}
                </div>
            )}
            {showSuccess && (
                <div className={styles.successMessage}>
                    ✓ Email accepteret
                </div>
            )}
            {error && (
                <div className={styles.serverError}>
                    {error}
                </div>
            )}
        </div>
    );
}
